import { useCallback, useState } from "react";

import { apiFetch } from "@pontimed/api-client";
import { isSupportedCountry } from "@pontimed/types";
import { Capacitor } from "@capacitor/core";

interface ReverseGeocodeResponse {
  /** ISO 3166-1 alpha-2 UPPERCASE per apps/api `/api/geocode` reverse contract */
  countryISO?: string;
  country?: string;
  city?: string;
  county?: string;
  formattedAddress?: string;
}

interface GeoIpResponse {
  /** Lowercase ISO country (already validated server-side against SUPPORTED_COUNTRIES) */
  country: string;
  locale: string;
  lat: number | null;
  lng: number | null;
  city: string | null;
}

/**
 * Browser/Capacitor-agnostic position read.
 * Resolves to coords on success or null on any failure (denial, timeout,
 * `kCLErrorLocationUnknown`, missing API). The picker falls back to IP-based
 * detection for every non-denial path, so callers get a "denied" signal vs.
 * generic failure to drive UX copy.
 */
async function getCurrentPosition(): Promise<
  { ok: true; lat: number; lng: number } | { ok: false; reason: "denied" | "unsupported" | "error" }
> {
  // Native — use Capacitor plugin so iOS/Android prompt the system dialog
  // instead of falling back to a browser stub. Dynamic import keeps the
  // plugin out of the web bundle.
  if (Capacitor.isNativePlatform()) {
    try {
      const { Geolocation } = await import("@capacitor/geolocation");
      const perm = await Geolocation.checkPermissions();
      if (perm.location !== "granted" && perm.coarseLocation !== "granted") {
        const req = await Geolocation.requestPermissions({ permissions: ["location"] });
        if (req.location !== "granted" && req.coarseLocation !== "granted") {
          return { ok: false, reason: "denied" };
        }
      }
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 10_000,
        maximumAge: 60_000,
      });
      return { ok: true, lat: pos.coords.latitude, lng: pos.coords.longitude };
    } catch (e) {
      const msg = e instanceof Error ? e.message.toLowerCase() : "";
      if (msg.includes("denied")) return { ok: false, reason: "denied" };
      return { ok: false, reason: "error" };
    }
  }

  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return { ok: false, reason: "unsupported" };
  }
  return new Promise((resolve) => {
    let settled = false;
    const finish = (
      r: { ok: true; lat: number; lng: number } | { ok: false; reason: "denied" | "error" },
    ) => {
      if (settled) return;
      settled = true;
      resolve(r);
    };
    // Hard timeout — built-in `timeout` is unreliable in some WebViews.
    const timer = setTimeout(() => finish({ ok: false, reason: "error" }), 8_000);
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(timer);
          finish({ ok: true, lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        (err) => {
          clearTimeout(timer);
          finish({
            ok: false,
            reason: err.code === err.PERMISSION_DENIED ? "denied" : "error",
          });
        },
        { enableHighAccuracy: false, timeout: 8_000, maximumAge: 60_000 },
      );
    } catch {
      clearTimeout(timer);
      finish({ ok: false, reason: "error" });
    }
  });
}

async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  try {
    const body = await apiFetch.get<ReverseGeocodeResponse>(
      `/api/geocode?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`,
    );
    const cc = body.countryISO?.toLowerCase();
    if (!cc || !isSupportedCountry(cc)) return null;
    return cc;
  } catch {
    return null;
  }
}

/**
 * Server-side IP geolocation fallback. Mirrors the `near me` flow in
 * `routes/_anon/find.tsx` — when GPS is unavailable (denial, system error,
 * `kCLErrorLocationUnknown`, browser blocks the API) we still try to land
 * the user on a reasonable country instead of forcing them to scroll the
 * globe. Returns null only when the IP lookup itself fails.
 */
async function detectCountryViaIp(): Promise<string | null> {
  try {
    const data = await apiFetch.get<GeoIpResponse>("/api/geoip");
    const cc = data.country?.toLowerCase();
    if (cc && isSupportedCountry(cc)) return cc;
  } catch {
    // ignore — caller surfaces error status
  }
  return null;
}

export type DetectStatus =
  | "idle"
  | "requesting-permission"
  | "geocoding"
  | "success"
  | "denied"
  | "unsupported"
  | "error";

interface UseDetectCountry {
  detect: () => Promise<string | null>;
  status: DetectStatus;
  /** Truthy while a detect call is in flight (for button disabled state) */
  loading: boolean;
  error: string | null;
}

/**
 * Detect the user's country via geolocation + reverse-geocode.
 *
 * MUST be invoked from a user gesture (click handler) — both iOS Safari and
 * the Capacitor plugin require an active user action to surface the system
 * permission prompt. Calling on mount triggers a silent denial.
 *
 * Returns the lowercase ISO 3166-1 alpha-2 country code on success, `null`
 * on denial or any failure path. Errors are swallowed and surfaced via
 * `status` + `error` for the caller to render.
 */
export function useDetectCountry(): UseDetectCountry {
  const [status, setStatus] = useState<DetectStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const detect = useCallback(async (): Promise<string | null> => {
    setError(null);
    setStatus("requesting-permission");

    const gps = await getCurrentPosition();

    if (gps.ok) {
      setStatus("geocoding");
      const cc = await reverseGeocode(gps.lat, gps.lng);
      if (cc) {
        setStatus("success");
        return cc;
      }
      // Reverse-geocode came back empty / unsupported country — fall through
      // to IP so we still hand the user a reasonable default.
    }

    // Permission denied → don't silently look up via IP; user said "no".
    // The picker shows the "denied" copy and lets them tap on the map.
    if (!gps.ok && gps.reason === "denied") {
      setStatus("denied");
      setError("permission-denied");
      return null;
    }

    // GPS unavailable / errored (incl. iOS `kCLErrorLocationUnknown`) — try
    // server-side IP geolocation. Same fallback as `near me` in /find.
    setStatus("geocoding");
    const ipCc = await detectCountryViaIp();
    if (ipCc) {
      setStatus("success");
      return ipCc;
    }

    setStatus(!gps.ok && gps.reason === "unsupported" ? "unsupported" : "error");
    setError(!gps.ok ? gps.reason : "geocode-failed");
    return null;
  }, []);

  const loading = status === "requesting-permission" || status === "geocoding";

  return { detect, status, loading, error };
}
